var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, Button, FormControl, FormHelperText, Typography, } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SignaturePad from "signature_pad";
import { buildFieldValidation } from "../../validators/field-components";
import CommentArea from "../common/CommentArea";
import { useElementConditions } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, } from "../../../../utils/forms-functions";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
var DEFAULT_SIGNATURE_WIDTH = 300;
var DEFAULT_SIGNATURE_HEIGHT = 200;
var getMimeType = function (dataFormat) {
    switch (dataFormat) {
        case "jpeg":
            return "image/jpeg";
        case "svg":
            return "image/svg+xml";
        default:
            return "image/png";
    }
};
var SignaturePadField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormContext(), unregister = _b.unregister, getValues = _b.getValues;
    var error = errors[element.name];
    var _c = useElementConditions(element), isVisible = _c.isVisible, isEnabled = _c.isEnabled, isInteractive = _c.isInteractive, fieldRequired = _c.isRequired;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [element.name, getCommentFieldName(element.name)]
            : [element.name];
    }, [element.name, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var width = element.signatureWidth || DEFAULT_SIGNATURE_WIDTH;
    var height = element.signatureHeight || DEFAULT_SIGNATURE_HEIGHT;
    var mimeType = getMimeType(element.dataFormat);
    var showPlaceholder = element.showPlaceholder !== false;
    var placeholderText = isInteractive
        ? element.placeholder
        : element.placeholderReadOnly || element.placeholder;
    var initialValue = getInitialValueForElement(element);
    var signaturePadRef = useRef(null);
    var _d = useState(!(initialValue === null || initialValue === void 0 ? void 0 : initialValue.content)), isEmpty = _d[0], setIsEmpty = _d[1];
    var isEnabledRef = useRef(isInteractive);
    useEffect(function () {
        isEnabledRef.current = isInteractive;
    }, [isInteractive]);
    var canvasCallbackRef = useCallback(function (node) {
        var _a, _b;
        if (!node) {
            (_a = signaturePadRef.current) === null || _a === void 0 ? void 0 : _a.off();
            signaturePadRef.current = null;
            return;
        }
        var ratio = Math.max(window.devicePixelRatio || 1, 1);
        node.width = width * ratio;
        node.height = height * ratio;
        (_b = node.getContext("2d")) === null || _b === void 0 ? void 0 : _b.scale(ratio, ratio);
        var pad = new SignaturePad(node, {
            penColor: element.penColor || "#1ab394",
            backgroundColor: element.backgroundImage
                ? "rgba(0,0,0,0)"
                : element.backgroundColor || "#ffffff",
            minWidth: element.penMinWidth || 0.5,
            maxWidth: element.penMaxWidth || 2.5,
        });
        if (!isEnabledRef.current)
            pad.off();
        signaturePadRef.current = pad;
        var existingValue = getValues(element.name);
        if (existingValue === null || existingValue === void 0 ? void 0 : existingValue.content) {
            pad
                .fromDataURL(existingValue.content, { width: width, height: height })
                .then(function () { return setIsEmpty(false); })
                .catch(function () { });
        }
    }, [width, height, element, getValues]);
    useEffect(function () {
        var pad = signaturePadRef.current;
        if (!pad)
            return;
        isInteractive ? pad.on() : pad.off();
    }, [isInteractive]);
    var handleEndStroke = function (onChange) {
        var pad = signaturePadRef.current;
        if (!pad || !isInteractive || pad.isEmpty())
            return;
        var content = pad.toDataURL(mimeType);
        setIsEmpty(false);
        onChange({
            name: "".concat(element.name, ".").concat(element.dataFormat || "png"),
            type: mimeType,
            content: content,
        });
    };
    var handleClear = function (onChange) {
        var _a;
        (_a = signaturePadRef.current) === null || _a === void 0 ? void 0 : _a.clear();
        setIsEmpty(true);
        onChange(null);
    };
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: element.name, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            return (_jsxs(Box, { children: [_jsxs(Box, __assign({ sx: {
                                            position: "relative",
                                            width: width,
                                            height: height,
                                            border: "1px solid",
                                            borderColor: error ? "error.main" : "divider",
                                            borderRadius: 1,
                                            backgroundImage: element.backgroundImage
                                                ? "url(".concat(element.backgroundImage, ")")
                                                : undefined,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            backgroundColor: element.backgroundImage
                                                ? undefined
                                                : element.backgroundColor || "#ffffff",
                                            opacity: isInteractive ? 1 : 0.6,
                                        } }, { children: [_jsx("canvas", { ref: canvasCallbackRef, style: {
                                                    width: width,
                                                    height: height,
                                                    touchAction: "none",
                                                    display: "block",
                                                }, onPointerUp: function () { return handleEndStroke(field.onChange); } }), showPlaceholder && isEmpty && placeholderText && (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: {
                                                    position: "absolute",
                                                    bottom: 8,
                                                    left: 0,
                                                    right: 0,
                                                    textAlign: "center",
                                                    pointerEvents: "none",
                                                } }, { children: placeholderText })))] })), element.allowClear !== false && isInteractive && (_jsx(Button, __assign({ size: "small", startIcon: _jsx(ClearIcon, {}), onClick: function () { return handleClear(field.onChange); }, sx: { mt: 1 } }, { children: "Limpar assinatura" }))), error && (_jsx(FormHelperText, __assign({ error: true }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default SignaturePadField;
